import React, { useEffect, useRef, useState } from 'react';

/**
 * CountUp — animates a number from 0 to `end` once it scrolls into view.
 * Years (e.g. 2012) and non-numeric values pass straight through unchanged.
 *
 * Props:
 *  - end (string|number)   — the target value (e.g. '370', '720', '2012')
 *  - duration (ms)         — animation duration (default 1600)
 *  - className (string)    — passed to wrapping <span>
 */
export default function CountUp({ end, duration = 1600, className = '' }) {
  const ref = useRef(null);
  const [display, setDisplay] = useState(0);
  const startedRef = useRef(false);

  // Parse the target. Treat 4-digit years (1900-2100) and non-pure-numerics as static.
  const raw = String(end ?? '');
  const isPureInt = /^\d+$/.test(raw);
  const numeric = isPureInt ? parseInt(raw, 10) : NaN;
  const isYear = isPureInt && raw.length === 4 && numeric >= 1900 && numeric <= 2100;
  const animate = isPureInt && !isYear;

  useEffect(() => {
    if (!animate) return;
    const node = ref.current;
    if (!node) return;

    const start = () => {
      if (startedRef.current) return;
      startedRef.current = true;
      const t0 = performance.now();
      const tick = (now) => {
        const t = Math.min(1, (now - t0) / duration);
        // easeOutCubic
        const eased = 1 - Math.pow(1 - t, 3);
        setDisplay(Math.round(numeric * eased));
        if (t < 1) requestAnimationFrame(tick);
        else setDisplay(numeric);
      };
      requestAnimationFrame(tick);
    };

    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) start(); });
    }, { threshold: 0.35 });
    io.observe(node);
    return () => io.disconnect();
  }, [animate, numeric, duration]);

  if (!animate) {
    // Non-animated (years, e.g. '2012', or non-numeric like '720+')
    return <span ref={ref} className={className}>{raw}</span>;
  }

  return <span ref={ref} className={className}>{display}</span>;
}
